import { useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { ApiError } from "@/api/client";
import { authApi } from "@/api/endpoints";
import { useAuth } from "@/auth/use-auth";
import type { UserRole } from "@/api/types";
import { AuthShell } from "@/components/auth/AuthShell";
import { Button } from "@/components/ui/Button";
import { Field, SelectField } from "@/components/ui/Field";
import { Note } from "@/components/ui/Note";
import { useI18n } from "@/i18n/use-i18n";
import { cn } from "@/lib/cn";

type Mode = "signin" | "signup" | "forgot";

export function LoginPage() {
  const { login } = useAuth();
  const { t } = useI18n();
  const [mode, setMode] = useState<Mode>("signin");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<UserRole>("vertrieb");

  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Public endpoint: decides whether registration is offered at all.
  const policy = useQuery({ queryKey: ["signup-policy"], queryFn: authApi.policy });
  const domains = policy.data?.allowed_email_domains ?? [];

  function switchMode(next: Mode) {
    setMode(next);
    setError(null);
    setSent(null);
    setPassword("");
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSent(null);

    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setError(t("login.error.email"));
      return;
    }
    if (mode === "signin" && !password) {
      setError(t("login.error.password"));
      return;
    }
    if (mode === "signup" && !name.trim()) {
      setError(t("login.error.name"));
      return;
    }

    setBusy(true);
    try {
      if (mode === "signin") {
        await login(trimmedEmail, password);
        // No navigate(): once `user` is set the router swaps in the app shell.
      } else if (mode === "signup") {
        // effectiveRole, not role: send what the form is showing. The server
        // forces the same thing, but agreeing with it keeps the two honest.
        const result = await authApi.signup(name.trim(), trimmedEmail, effectiveRole);
        setSent(result.message);
      } else {
        const result = await authApi.forgotPassword(trimmedEmail);
        setSent(result.message);
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t("login.error.generic"));
    } finally {
      setBusy(false);
    }
  }

  const signupOffered = policy.data?.signup_enabled ?? false;
  const partnerSignup = policy.data?.partner_signup_enabled ?? false;

  // Mirrors auth_service.is_allowed_domain: exact match on the part after the
  // last "@", no subdomains. Only meaningful once an "@" has been typed, so a
  // half-written address is not called external.
  const typedDomain = email.trim().toLowerCase().split("@")[1] ?? "";
  const external =
    partnerSignup && typedDomain.length > 0 && !domains.includes(typedDomain);
  // The server overrules the dropdown for an outside address, so the form must
  // not pretend the choice is still open.
  const effectiveRole: UserRole = external ? "vertriebspartner" : role;
  const domainList = domains.map((domain) => `@${domain}`).join(t("login.domainOr"));

  return (
    <AuthShell>
      {/* Mode switch. Hidden entirely when self-registration is disabled, so
          the UI never offers something the backend will refuse. */}
      {signupOffered && (
        <div className="mb-6 flex gap-1 rounded-[10px] bg-page p-1">
          {(["signin", "signup"] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={mode === option}
              onClick={() => switchMode(option)}
              className={cn(
                "min-h-11 flex-1 rounded-[7px] px-3 text-[13.5px] font-semibold transition-colors sm:min-h-0 sm:py-2",
                mode === option || (mode === "forgot" && option === "signin")
                  ? "bg-surface text-ink shadow-card"
                  : "text-muted hover:text-ink",
              )}
            >
              {t(`login.${option}.title`)}
            </button>
          ))}
        </div>
      )}

      <h1 className="mb-1.5 text-2xl">{t(`login.${mode}.title`)}</h1>
      <p className="mb-6 text-[13.6px] text-muted">{t(`login.${mode}.intro`)}</p>

      {sent ? (
        <>
          {/* The message comes from the backend, so it stays German. */}
          <Note tone="ok">{sent}</Note>
          <p className="mt-4 text-[13px] text-muted">{t("login.sentHint")}</p>
          <Button variant="ghost" className="mt-4 w-full" onClick={() => switchMode("signin")}>
            {t("login.backToSignin")}
          </Button>
        </>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          {mode === "signup" && (
            <>
              <Field
                label={t("login.field.name")}
                autoComplete="name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                className="mb-4"
              />
              {/* Options come from the server, so the dropdown can never offer a
                  role signup would reject. Management is not among them: it sees
                  every deal and may create users, so it stays manager-assigned.
                  An outside address is pinned to Vertriebspartner because that is
                  what the server will store either way. */}
              <SelectField
                label={t("login.field.role")}
                value={effectiveRole}
                onChange={(value) => setRole(value as UserRole)}
                disabled={external}
                options={(policy.data?.selectable_roles ?? []).map((option) => ({
                  value: option,
                  label: t(`role.${option}`),
                }))}
                className={external ? "mb-1.5" : "mb-4"}
              />
              {external && (
                <p className="mb-4 text-[12.5px] text-muted">
                  {t("login.forcedPartner", { domains: domainList })}
                </p>
              )}
            </>
          )}

          <Field
            label={t("login.field.email")}
            type="email"
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="mb-4"
          />

          {/* With partner signup on the list is not a restriction, so saying
              "only these are permitted" would be false. */}
          {mode === "signup" && domains.length > 0 && (
            <p className="-mt-2 mb-4 text-[12.5px] text-muted">
              {t(partnerSignup ? "login.partnerHint" : "login.domainHint", {
                domains: domainList,
              })}
            </p>
          )}

          {mode === "signin" && (
            <Field
              label={t("login.field.password")}
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          )}

          {error && (
            <Note tone="error" className="mt-4">
              {error}
            </Note>
          )}

          <Button type="submit" busy={busy} className="mt-5 w-full">
            {t(`login.${mode}.submit`)}
          </Button>

          <div className="mt-4 text-center">
            {mode === "signin" ? (
              <button
                type="button"
                onClick={() => switchMode("forgot")}
                className="inline-flex min-h-11 items-center justify-center border-0 bg-transparent px-2 text-[12.5px] text-muted underline hover:text-ink"
              >
                {t("login.forgotLink")}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => switchMode("signin")}
                className="inline-flex min-h-11 items-center justify-center border-0 bg-transparent px-2 text-[12.5px] text-muted underline hover:text-ink"
              >
                {t("login.backToSignin")}
              </button>
            )}
          </div>
        </form>
      )}
    </AuthShell>
  );
}
